import { useState, type FormEvent } from 'react'
import { adminApi, ApiError } from '../lib/api'

const BLANK = {
  slug: '',
  name: '',
  admin_login: '',
  admin_display_name: '',
  admin_password: '',
}

/** Bringing a customer online is genuinely this one action.
 *
 *  DNS is a wildcard and the certificate is fetched on first visit, gated on
 *  the organization existing — so there is no DNS record to add, no Caddyfile
 *  edit, no restart and no deploy. */
export function NewCustomer({
  onCreated,
  onCancel,
}: {
  onCreated: (slug: string) => void
  onCancel: () => void
}) {
  const [form, setForm] = useState({ ...BLANK })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<{ url: string; login: string; slug: string } | null>(null)

  async function submit(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const created = await adminApi.organizationCreate(form)
      setDone({ url: created.url, login: form.admin_login, slug: created.slug })
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not create that customer.')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="pad">
        <h2>Customer created</h2>
        <p className="blurb">
          Live now at <strong>{done.url}</strong>. The certificate is fetched
          the first time somebody visits — no DNS change, no restart.
        </p>
        <div className="summary">
          <div><span className="k">Address</span>{done.url}</div>
          <div><span className="k">Their login</span>{done.login}</div>
          <div><span className="k">Password</span>the one you just set</div>
        </div>
        <p className="footnote">
          Their nature-of-call list is deliberately empty. It has to be their
          own real categories — placeholders become real records the first day
          somebody dispatches on them. Their admin adds those after signing in.
        </p>
        <div className="btnrow">
          <button className="primary compact" onClick={() => onCreated(done.slug)}>
            Configure this customer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="pad">
      <h2>New customer</h2>
      <p className="blurb">
        The short name becomes their web address, so it has to be a legal
        hostname label: lowercase letters, digits and hyphens.
      </p>

      {error && <div className="notice">{error}</div>}

      <form onSubmit={submit}>
        <div className="grid2">
          <div className="field">
            <label>Short name — becomes the address</label>
            <input
              value={form.slug}
              placeholder="abc"
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              required
            />
            {form.slug && (
              <p className="footnote mono">
                https://{form.slug.toLowerCase()}.crm.advancetalk.ca
              </p>
            )}
          </div>
          <div className="field">
            <label>Organization name</label>
            <input
              value={form.name}
              placeholder="ABC Emergency Response"
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </div>
        </div>

        <h3 className="sub">Their first login</h3>
        <p className="blurb">
          There is no sign-up screen anywhere in this product — only
          dispatchers log in, and their accounts are made for them. This is the
          account you hand over; their admin creates the rest.
        </p>

        <div className="grid2">
          <div className="field">
            <label>Login</label>
            <input
              value={form.admin_login}
              onChange={(e) => setForm({ ...form, admin_login: e.target.value })}
              required
            />
          </div>
          <div className="field">
            <label>Their name</label>
            <input
              value={form.admin_display_name}
              onChange={(e) =>
                setForm({ ...form, admin_display_name: e.target.value })
              }
              required
            />
          </div>
        </div>

        <div className="field">
          <label>Password — 12 characters or more</label>
          <input
            type="password"
            value={form.admin_password}
            onChange={(e) => setForm({ ...form, admin_password: e.target.value })}
            required
          />
        </div>

        <div className="btnrow">
          <button className="primary compact" disabled={busy}>
            {busy ? 'Creating…' : 'Create customer'}
          </button>
          <button type="button" className="ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  )
}
